import FeatureLandingPage from "@/components/features/FeatureLandingPage";

const SITE = "https://shattaraai.com";

const howItWorks = [
  { title: "Choose Your Sources", description: "Select the materials, topics, or chapters you want the exam to cover." },
  { title: "Configure the Exam", description: "Set question count, types, and time limit. Shattara generates a full exam matching your preferences." },
  { title: "Take the Exam Under Real Conditions", description: "A countdown timer runs while you answer. Submit when ready or when time expires." },
  { title: "Review Your Results", description: "Get a detailed score breakdown, question-by-question explanations, and a summary of weak areas." },
];

const benefits = [
  { title: "Simulate Real Exam Conditions", description: "Timed exams with question limits help you build speed and manage test anxiety." },
  { title: "Covers Your Exact Curriculum", description: "Exams are generated from your uploaded materials — not generic question banks." },
  { title: "Detailed Score Reports", description: "See your total score, time spent, and a breakdown of correct and incorrect answers." },
  { title: "Identify Weak Areas Before the Real Test", description: "The summary highlights topics where you lost points so you can focus your review." },
  { title: "Multiple Attempts", description: "Retake exams to track improvement over time and build confidence." },
];

const faqItems = [
  { question: "How long can an AI-generated exam be?", answer: "You choose the number of questions and time limit. Exams can range from 10 questions to 100+, with time limits from 15 minutes to several hours." },
  { question: "What question types are supported?", answer: "Exams can include multiple-choice, true/false, and open-ended questions. You select which types to include when configuring the exam." },
  { question: "Is the exam timed?", answer: "Yes. You set a time limit and a countdown runs during the exam. You can submit early or the exam auto-submits when time expires." },
  { question: "Can I review my answers after submitting?", answer: "Yes. After submission you see a full summary with your score, each question, your answer, the correct answer, and an explanation." },
  { question: "How is this different from the quiz generator?", answer: "Exams are designed to simulate real test conditions — longer, timed, and with a comprehensive score report. Quizzes are shorter, topic-focused practice sessions." },
];

const relatedFeatures = [
  { name: "AI Tutor", href: "/ai-tutor", description: "Get step-by-step help understanding concepts you missed on the exam." },
  { name: "AI Flashcard Generator", href: "/ai-flashcards", description: "Create flashcards from topics you need to review after exam results." },
  { name: "AI Quiz Generator", href: "/ai-quiz-generator", description: "Do quick topic-focused practice before a full exam simulation." },
  { name: "AI Lecture Summarizer", href: "/ai-lecture-summarizer", description: "Summarize lectures to review key points before your exam." },
  { name: "Best AI Study Tools", href: "/best-ai-study-tools", description: "Compare the top AI-powered study tools for students in 2026." },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Shattara AI Exam Generator",
    description: "Generate timed, scored exams from your own study materials with detailed results and explanations.",
    url: `${SITE}/ai-exam-generator`,
    brand: { "@type": "Organization", name: "Shattara AI" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Generate AI Exams with Shattara",
    description: "Create full exam simulations from your course materials using Shattara AI.",
    step: howItWorks.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.description,
    })),
  },
];

export default function AIExamGeneratorPage() {
  return (
    <FeatureLandingPage
      seoTitle="AI Exam Generator — Shattara AI"
      seoDescription="Generate full practice exams tailored to your syllabus and uploaded study materials."
      headline="AI Exam Generator — Know You're Ready Before Exam Day"
      subtitle="Configure timed exams from your own materials. Get scored results with question-by-question explanations and weak-area analysis."
      howItWorks={howItWorks}
      benefits={benefits}
      faqItems={faqItems}
      relatedFeatures={relatedFeatures}
      structuredData={structuredData}
    />
  );
}
